import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import type { MgaPeriod } from '../useData';
import type { MgaRemittanceRow } from '../useData';
import { MgaReportRoot } from './MgaReportRoot';
import { ReportHeader } from './ReportHeader';
import { formatReportMoney, reportNum, reportRound2 } from './reportMeta';

export type RemittanceReportProps = {
  period: MgaPeriod;
  rows: MgaRemittanceRow[];
  companyLogoSrc?: string | null;
  companyLogoAlt?: string;
};

export const RemittanceReport: React.FC<RemittanceReportProps> = ({
  period,
  rows,
  companyLogoSrc = null,
  companyLogoAlt,
}) => {
  const ccy = period.currency || 'USD';
  const total = useMemo(
    () => reportRound2(rows.reduce((s, r) => s + reportNum(r.amount), 0)),
    [rows],
  );

  return (
    <MgaReportRoot>
      <ReportHeader
        period={period}
        title="Allotment remittance"
        companyLogoSrc={companyLogoSrc}
        companyLogoAlt={companyLogoAlt}
      />

      <section className="mga-report-section">
        <table className="mga-report-table">
          <thead>
            <tr>
              <th className="mga-report-num">A/A</th>
              <th>Crew</th>
              <th>Rank</th>
              <th>Allottee</th>
              <th>Bank</th>
              <th>Account</th>
              <th className="mga-report-num">Amount</th>
              <th>CCY</th>
              <th>Date</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="mga-report-muted">
                  No allotment remittance rows
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.mgaCrewRequestId ?? i}-${r.crewName}`}>
                  <td className="mga-report-num">{i + 1}</td>
                  <td>{r.crewName}</td>
                  <td>{r.rankName || '—'}</td>
                  <td>{r.allotteeName || '—'}</td>
                  <td>{r.bank || '—'}</td>
                  <td>{r.bankAccount || '—'}</td>
                  <td className="mga-report-num">{formatReportMoney(r.amount)}</td>
                  <td>{r.currency || ccy}</td>
                  <td>{r.entryDate ? formatDateGlobal(r.entryDate) : '—'}</td>
                  <td>{r.remarks || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr>
                <td colSpan={6} className="mga-report-emph">
                  Total ({rows.length})
                </td>
                <td className="mga-report-num mga-report-emph">{formatReportMoney(total)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </section>
    </MgaReportRoot>
  );
};

export default RemittanceReport;
